import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, Optional, Renderer2, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { UndefinedOption } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ShellComponent } from '../../../../ui/display/local-shell.component';
import { MinibasketComponent } from './../../../../basket/minibasket/minibasket.component';

let uid = 0;

export interface IPageComponentCategoryCarouselComponentData extends NICmsContentComponent.IData {
    title: string;
    copy?: string;
    showCarouselOnFacets?: string;
    cta_copy?: string;
    cta_link?: string;
    elements_range: CategoryImages[];
    elements_category: CategoryImages[];
    variant: ECategoryCarouselVariant;
    fullWidthVariant?: 'fullWidth' | 'defaultWidth';
    fullWidthMobileSlides?: 'big' | 'small';
}
interface CategoryImages {
    image: string;
    category_title: string;
    category_subtitle_line?: string;
    category_subtitle_line_2?: string;
    url?: string;
    anchorId?: string;
    isAnchorLink?: boolean;
}
enum ECategoryCarouselVariant {
    category = 'variant_category_carousel',
    range = 'variant_range_carousel',
    categoryCircles = 'variant_category_circles',
    categorySmallSquares = 'variant_category_small_squares'
}
@Component({
    selector: 'page-component-category-carousel',
    templateUrl: 'category-carousel.html',
    styleUrls: ['category-carousel.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, ShellComponent, HrefComponent, ICmsClientModule, CarouselModule, DfImageDirective]
})
@ICmsComponent<PageComponentCategoryCarouselComponent>({
    id: 'category-carousel',
    name: 'Category Carousel Layout',
    tags: ['Category', 'Carousel'],
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'copy',
            title: 'Copy'
        },
        NICmsContentComponent.backgroundField,
        <NICmsContentField.IChoiceField>{
            key: 'showCarouselOnFacets',
            type: NICmsContentField.EFieldType.choice,
            title: 'Show carousel on facets',
            default: 'show',
            options: [
                { key: 'show', title: 'Show' },
                { key: 'hide', title: 'Hide' }
            ]
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_copy',
            title: 'CTA copy'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_link',
            title: 'CTA link'
        },
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Variant',
            required: true,
            options: [
                UndefinedOption,
                { key: ECategoryCarouselVariant.category, title: 'Category Carousel' },
                { key: ECategoryCarouselVariant.range, title: 'Range Carousel' },
                { key: ECategoryCarouselVariant.categoryCircles, title: 'Category Carousel Circles' },
                { key: ECategoryCarouselVariant.categorySmallSquares, title: 'Category Carousel Small Squares' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'fullWidthVariant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Category Carousel Full Width',
            options: [
                ...UndefinedOption,
                { key: 'fullWidth', title: 'Full width variant' },
                { key: 'defaultWidth', title: 'Default width variant' }
            ],
            default: 'defaultWidth'
        },
        <NICmsContentField.IChoiceField>{
            key: 'fullWidthMobileSlides',
            type: NICmsContentField.EFieldType.choice,
            title: 'Slides per view (mobile)',
            options: [
                { key: 'big', title: '1.3 slides per view' },
                { key: 'small', title: '2.3 slides per view' }
            ],
            default: 'small',
            canShow: data => data?.fullWidthVariant === 'fullWidth'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements_category',
            title: 'Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            itemNameField: 'itemTitle',
            defaultSize: 4,
            canShow: (data: IPageComponentCategoryCarouselComponentData): boolean =>
                data?.variant === ECategoryCarouselVariant.category ||
                data?.variant === ECategoryCarouselVariant.categorySmallSquares ||
                data?.variant === ECategoryCarouselVariant.categoryCircles,
            fields: [
                <NICmsContentField.IField>{
                    key: 'itemTitle',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item title',
                    description: 'Not used on website, used for iCMS item name only!'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'category_title',
                    title: 'Category Title'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'category_subtitle',
                    title: 'Category Subtitle'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'category_subtitle_line_2',
                    title: 'Category Subtitle line 2'
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'isAnchorLink',
                    title: 'Anchor Link'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'url',
                    title: 'Link',
                    canShow: data => !data?.isAnchorLink
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'anchorId',
                    title: 'Anchor ID',
                    canShow: data => data?.isAnchorLink
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements_range',
            title: 'Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            itemNameField: 'itemTitle',
            defaultSize: 4,
            minSize: 4,
            canShow: (data: IPageComponentCategoryCarouselComponentData): boolean => data?.variant === ECategoryCarouselVariant.range,
            fields: [
                <NICmsContentField.IField>{
                    key: 'itemTitle',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item title',
                    description: 'Not used on website, used for iCMS item name only!'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image'
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'isAnchorLink',
                    title: 'Anchor Link'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'url',
                    title: 'Link',
                    canShow: data => !data?.isAnchorLink
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'anchorId',
                    title: 'Anchor ID',
                    canShow: data => data?.isAnchorLink
                }
            ]
        }
    ]
})
export class PageComponentCategoryCarouselComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentCategoryCarouselComponentData;
    variantTypes = ECategoryCarouselVariant;

    uid = `category-carousel-${uid++}`;

    constructor(
        @Inject(DOCUMENT)
        private document: Document,
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        private app: App,
        @Optional()
        private minibasetComponent: MinibasketComponent,
        private meta: Meta
    ) {
        super();
    }

    override ngOnInit(): void {
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.hideOnFacet();
                }
            })
        );
    }

    private scrollToService = inject(ScrollToService);

    get elements(): CategoryImages[] {
        if (this.component) {
            switch (this.component.variant) {
                case ECategoryCarouselVariant.category:
                case ECategoryCarouselVariant.categoryCircles:
                case ECategoryCarouselVariant.categorySmallSquares:
                    return this.component.elements_category;
                case ECategoryCarouselVariant.range:
                    return this.component.elements_range;
                default:
                    return [];
            }
        }
        return [];
    }
    get variant(): ECategoryCarouselVariant | undefined {
        return this.component?.variant;
    }
    get isMinibasket(): boolean {
        return !!this.minibasetComponent;
    }

    get isFullWidth(): boolean {
        return this.component?.fullWidthVariant === 'fullWidth';
    }

    get slidesPerView(): number {
        if (this.isMinibasket) {
            return 1.5;
        }
        if (this.isFullWidth) {
            if (this.component.fullWidthMobileSlides === 'big') {
                return [1.3, 1.3, 5.25, 5.25][this.client.sizeId];
            } else {
                return [2.3, 2.3, 5.25, 5.25][this.client.sizeId];
            }
        }
        return this.variant === ECategoryCarouselVariant.categorySmallSquares
            ? [1.5, 3.15, 4.5, 4.5][this.client.sizeId]
            : [1.5, 3.15, 3.75, 3.75][this.client.sizeId];
    }

    get pcSlides() {
        return this.slidesPerView;
    }

    get pcSlidesCeil() {
        return Math.ceil(this.pcSlides);
    }

    get pcGutter() {
        return 20 + 'px';
    }

    get loopedSlides(): number | undefined {
        return this.isFullWidth ? Math.ceil(this.slidesPerView) : undefined;
    }

    get isFacet(): boolean {
        return !!this.app.meta.state?.route?.data?.facet?.category;
    }

    hideOnFacet() {
        this.isFacet && this.component.showCarouselOnFacets === 'hide'
            ? this.elementRef.nativeElement.classList.add('ng-hide')
            : this.elementRef.nativeElement.classList.remove('ng-hide');

        this.detectChanges();
    }

    scrollToId(id: string): void {
        const element = this.document.body.querySelector('#' + id);
        if (element) {
            this.scrollToService.scrollTo(element, { offset: 120 });
        }
    }
}
